import React, { useEffect, useMemo } from 'react';
import { useGlobalization } from '@Translations/useGlobalization';
import { useHistory } from 'react-router-dom';
import { Button, StylovyzeComposableDialog, StylovyzeFormV2, InputText, InputSelect } from '@innovyze/stylovyze';
import { useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import { useAssetSchema } from '@Hooks/assetSchemaHooks';
import { usePickerOptions } from '@Hooks/useSystemTypes';
import { PickerStatus } from '@Components/Utils/PickerStatus';
import { addRouteNamespace } from '@Utils/actions';
import { InspectionStandardType } from '@Types/inspections.types';
import { selectFailureDefinitionsCards } from '@Selectors/failureDefinitions.selector';
import { listAllFailureDefinitions } from '@Actions/failureDefinition.actions';
import { isAlphanumericName, isBlankName, isDuplicateName } from '@Utils/nameValidation';

interface DefineFailureDefinitionDialogProps {
	isOpen: boolean;
	setOpen: (arg0: boolean) => void;
}

const AssetTypeField = () => {
	const { t } = useGlobalization();
	const {
		values: { systemType },
		errors,
	} = useFormikContext<{
		systemType: string;
		assetType: string;
	}>();
	const { assetTypesFor } = usePickerOptions();
	const assetTypes = systemType ? assetTypesFor(systemType) : undefined;

	return (
		<InputSelect
			menuItems={
				assetTypes
					? assetTypes
							.filter(at => at.displayName === 'Pipe')
							.map(at => ({
								key: at.id,
								value: at.id,
								label: t(at.displayName),
							}))
					: []
			}
			fieldAttrs={{ name: 'assetType' }}
			selectProps={{
				label: t('Asset Type'),
				fullWidth: true,
				error: !!errors.assetType,
			}}
			helperText={errors.assetType ? errors.assetType.toString() : ''}
		/>
	);
};

const InspectionStandardField = () => {
	const { t } = useGlobalization();
	const {
		values: { assetType, systemType },
		errors,
	} = useFormikContext<{
		assetType: string;
		systemType: string;
		inspectionStandard: string;
	}>();

	// Display read, so it uses the open-flag hooks. Pending and error leave the standards empty.
	const { data: assetSchemaByType } = useAssetSchema(systemType, assetType);

	const inspectionStandards = useMemo(() => {
		const inspectionTypes = [];
		if (assetSchemaByType?.hasInspectionType('PACP')) {
			inspectionTypes.push({
				key: InspectionStandardType.PACP,
				value: InspectionStandardType.PACP,
				label: t('PACP'),
			});
		}
		if (assetSchemaByType?.hasInspectionType('WSA05')) {
			inspectionTypes.push({
				key: InspectionStandardType.WSA05,
				value: InspectionStandardType.WSA05,
				label: t('WSA05'),
			});
		}

		return inspectionTypes;
	}, [assetSchemaByType, t]);

	// Don't show for WaterDistribution system type
	if (systemType === 'WaterDistribution') {
		return <></>;
	}

	if (inspectionStandards.length === 0) {
		return <></>;
	}

	return (
		<InputSelect
			menuItems={inspectionStandards}
			fieldAttrs={{ name: 'inspectionStandard' }}
			selectProps={{
				label: t('Inspection Standard'),
				fullWidth: true,
				error: !!errors.inspectionStandard,
			}}
			helperText={errors.inspectionStandard ? errors.inspectionStandard.toString() : ''}
		/>
	);
};

export const DefineFailureDefinitionDialog = ({ setOpen, isOpen }: DefineFailureDefinitionDialogProps) => {
	const { t } = useGlobalization();
	const history = useHistory();

	const dispatch = useDispatch();

	const { systemTypes, status: pickerStatus, retry: retryPicker } = usePickerOptions();
	const failureDefinitionsCards = selectFailureDefinitionsCards();

	useEffect(() => {
		dispatch(listAllFailureDefinitions());
	}, []);

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const handleSubmit = (values, { setSubmitting }) => {
		setSubmitting(false);
		history.push(addRouteNamespace('failure-definition/new'), values);
	};

	return (
		<StylovyzeFormV2
			initialValues={{
				failureDefinitionName: '',
				systemType: '',
				assetType: '',
				inspectionStandard: '',
			}}
			validate={values => {
				const errors: { [key: string]: string } = {};
				if (isBlankName(values.failureDefinitionName)) {
					errors.failureDefinitionName = 'Required';
				} else if (!isAlphanumericName(values.failureDefinitionName)) {
					errors.failureDefinitionName = t('Only alphanumeric characters are allowed');
				} else if (
					isDuplicateName(
						values.failureDefinitionName,
						failureDefinitionsCards.failureDefinitions.map(fd => fd.name),
					)
				) {
					errors.failureDefinitionName = t('Enter a unique name');
				}
				if (!values.systemType) {
					errors.systemType = 'Required';
				}
				if (!values.assetType) {
					errors.assetType = 'Required';
				}
				// Only require inspectionStandard if systemType is not WaterDistribution
				if (values.systemType !== 'WaterDistribution' && !values.inspectionStandard) {
					errors.inspectionStandard = 'Required';
				}
				return errors;
			}}
			onSubmit={handleSubmit}>
			{({ submitForm, isSubmitting, errors }) => {
				return (
					<StylovyzeComposableDialog
						onCancel={() => setOpen(false)}
						open={isOpen}
						title={t('New Failure Definition')}
						footer={
							<>
								<Button variant="contained" color="secondary" onClick={() => setOpen(false)}>
									{t('Cancel')}
								</Button>
								<Button variant="contained" color="primary" disabled={isSubmitting} onClick={submitForm}>
									{t('Create Failure Definition')}
								</Button>
							</>
						}>
						<InputText
							fieldAttrs={{ name: 'failureDefinitionName' }}
							textField={{
								label: t('Failure Definition Name'),
								fullWidth: true,
								error: !!errors.failureDefinitionName,
								helperText: errors.failureDefinitionName ?? '',
							}}
						/>
						<InputSelect
							menuItems={systemTypes.map(st => ({
								key: st.id,
								value: st.id,
								label: t(st.displayName),
							}))}
							fieldAttrs={{ name: 'systemType' }}
							selectProps={{
								label: t('System Type'),
								fullWidth: true,
								error: !!errors.systemType,
							}}
							helperText={errors.systemType ? errors.systemType.toString() : ''}
						/>
						<PickerStatus status={pickerStatus} retry={retryPicker} />
						<AssetTypeField />
						<InspectionStandardField />
					</StylovyzeComposableDialog>
				);
			}}
		</StylovyzeFormV2>
	);
};
